(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';

  class ReviewsSection extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._list = this.querySelector('[data-reviews-list]');
      this._loadingEl = this.querySelector('[data-reviews-loading]');
      this._toolbar = this.querySelector('[data-reviews-count]')?.parentElement || this._list;
      this._moreBtn = this.querySelector('[data-reviews-more]');
      this._countEl = this.querySelector('[data-reviews-count]');
      this._sortEl = this.querySelector('[data-reviews-sort]');
      this._starBtns = Array.from(this.querySelectorAll('[data-star-filter]'));
      this._template = this.querySelector('[data-review-template]');

      this._pageSize = parseInt(this.getAttribute('data-page-size'), 10) || 3;
      this._totalCount = parseInt(this.getAttribute('data-total-count'), 10) || 0;
      this._archiveUrl = this.getAttribute('data-archive-url') || '';
      this._starSymbol = this.getAttribute('data-star-symbol') || '';
      this._starClip = this.getAttribute('data-star-clip') || '';
      this._archiveLoaded = false;
      this._archivePromise = null;
      this._visible = this._pageSize;
      this._starFilter = null;
      this._sortValue = 'latest';

      this._items = Array.from(this.querySelectorAll('[data-review]')).map(el => ({
        id: el.getAttribute('data-id') || '',
        rating: this._num(el.getAttribute('data-rating')),
        created: this._num(el.getAttribute('data-created')),
        helpful: this._num(el.getAttribute('data-helpful')),
        el,
      }));

      if (this._sortEl) {
        const toggle = this._sortEl.querySelector('[data-sort-toggle]');
        const dropdown = this._sortEl.querySelector('[data-sort-dropdown]');
        const label = this._sortEl.querySelector('[data-sort-label]');
        const chevron = this._sortEl.querySelector('[data-sort-chevron]');
        const options = Array.from(this._sortEl.querySelectorAll('[data-sort-value]'));

        toggle.addEventListener('click', () => {
          const isOpen = !dropdown.classList.contains('hidden');
          dropdown.classList.toggle('hidden');
          chevron.classList.toggle('rotate-180');
          if (!isOpen) {
            const close = (e) => {
              if (!this._sortEl.contains(e.target)) {
                dropdown.classList.add('hidden');
                chevron.classList.remove('rotate-180');
                document.removeEventListener('click', close);
              }
            };
            setTimeout(() => document.addEventListener('click', close), 0);
          }
        });

        options.forEach(btn => {
          btn.addEventListener('click', () => {
            this._sortValue = btn.getAttribute('data-sort-value');
            label.textContent = btn.textContent.trim();
            dropdown.classList.add('hidden');
            chevron.classList.remove('rotate-180');
            this._visible = this._pageSize;
            this._render();
          });
        });
      }

      this._moreBtn?.addEventListener('click', () => {
        this._visible += this._pageSize;
        this._render();
      });

      this._starBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const star = parseInt(btn.getAttribute('data-star-filter'), 10);
          this._starFilter = this._starFilter === star ? null : star;
          this._visible = this._pageSize;
          this._updateStarBtns();
          this._render();
          this._scrollToToolbar();
        });
      });

      this._render();
    }

    _scrollToToolbar() {
      const headerHeight = Array.from(document.querySelectorAll('.shopify-section-group-header-group'))
        .filter(el => /sticky|fixed/.test(getComputedStyle(el).position))
        .reduce((max, el) => Math.max(max, el.getBoundingClientRect().height), 0);
      const top = this._toolbar.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    _setLoading(isLoading) {
      if (this._loadingEl) {
        this._loadingEl.classList.toggle('hidden', !isLoading);
        this._loadingEl.classList.toggle('flex', isLoading);
      }
      if (this._moreBtn) this._moreBtn.disabled = isLoading;
      this.toggleAttribute('aria-busy', isLoading);
    }

    _num(value) {
      const v = Number(value || '0');
      return Number.isFinite(v) ? v : 0;
    }

    _updateStarBtns() {
      this._starBtns.forEach(btn => {
        const star = parseInt(btn.getAttribute('data-star-filter'), 10);
        const isActive = star === this._starFilter;
        btn.style.opacity = this._starFilter === null || isActive ? '' : '0.4';
      });
    }

    _needsArchive() {
      if (!this._archiveUrl || this._archiveLoaded) return false;
      if (this._sortValue !== 'latest' || this._starFilter !== null) return true;
      return this._visible > this._items.length && this._items.length < this._totalCount;
    }

    _loadArchive() {
      if (this._archivePromise) return this._archivePromise;
      this._archivePromise = fetch(this._archiveUrl, { credentials: 'omit' })
        .then(r => (r.ok ? r.text() : Promise.reject(new Error('archive ' + r.status))))
        .then(text => {
          const start = text.indexOf('{');
          const end = text.lastIndexOf('}');
          return JSON.parse(text.slice(start, end + 1));
        })
        .then(json => {
          const existing = new Map(this._items.map(item => [item.id, item]));
          this._items = (json.reviews || []).map(r => {
            const id = String(r.id);
            const known = existing.get(id);
            return {
              id,
              rating: this._num(r.rating),
              created: this._num(r.created_ts) || Math.floor(Date.parse(r.created_at) / 1000) || 0,
              helpful: this._num(r.helpful_up),
              data: r,
              el: known ? known.el : null,
            };
          });
          this._archiveLoaded = true;
        })
        .catch(() => {
          this._archiveUrl = '';
        });
      return this._archivePromise;
    }

    _sorted(items) {
      const configs = {
        latest:  { key: 'created', desc: true },
        oldest:  { key: 'created', desc: false },
        highest: { key: 'rating',  desc: true },
        lowest:  { key: 'rating',  desc: false },
        helpful: { key: 'helpful', desc: true },
      };
      const cfg = configs[this._sortValue] || configs.latest;
      return items.slice().sort((a, b) => {
        const diff = cfg.desc ? b[cfg.key] - a[cfg.key] : a[cfg.key] - b[cfg.key];
        return diff !== 0 ? diff : b.created - a.created;
      });
    }

    _buildStars(svg, rating) {
      for (let i = 1; i <= 5; i++) {
        const x = (i - 1) * 24;
        if (i <= rating) {
          const use = document.createElementNS(SVG_NS, 'use');
          use.setAttribute('href', '#' + this._starSymbol);
          use.setAttribute('x', String(x));
          use.setAttribute('y', '0');
          use.setAttribute('width', '17');
          use.setAttribute('height', '16');
          use.setAttribute('fill', '#FF6A19');
          svg.appendChild(use);
        } else {
          const g = document.createElementNS(SVG_NS, 'g');
          g.setAttribute('transform', 'translate(' + x + ', 0)');
          const use = document.createElementNS(SVG_NS, 'use');
          use.setAttribute('href', '#' + this._starSymbol);
          use.setAttribute('width', '17');
          use.setAttribute('height', '16');
          use.setAttribute('fill', 'none');
          use.setAttribute('stroke', '#FF6A19');
          use.setAttribute('stroke-width', '2');
          use.setAttribute('clip-path', 'url(#' + this._starClip + ')');
          g.appendChild(use);
          svg.appendChild(g);
        }
      }
    }

    _createElement(item) {
      const r = item.data;
      const el = this._template.content.firstElementChild.cloneNode(true);
      el.setAttribute('data-id', item.id);
      el.setAttribute('data-created', String(item.created));
      el.setAttribute('data-rating', String(item.rating));
      el.setAttribute('data-helpful', String(item.helpful));
      this._buildStars(el.querySelector('[data-review-stars]'), item.rating);
      el.querySelector('[data-review-date]').textContent = r.date_label || '';
      el.querySelector('[data-review-author]').textContent = r.author || '';
      el.querySelector('[data-review-body]').textContent = r.body || '';
      return el;
    }

    _render() {
      if (this._needsArchive()) {
        this._setLoading(true);
        this._loadArchive().then(() => {
          this._setLoading(false);
          this._render();
        });
        return;
      }

      const filtered = this._starFilter !== null
        ? this._items.filter(item => item.rating === this._starFilter)
        : this._items;
      const ordered = this._sorted(filtered);
      const total = ordered.length;
      const shown = Math.min(this._visible, total);

      const visible = ordered.slice(0, shown);
      const visibleEls = new Set(visible.map(item => item.el).filter(Boolean));
      this._list.querySelectorAll('[data-review]').forEach(el => {
        if (!visibleEls.has(el)) el.classList.add('hidden');
      });

      const frag = document.createDocumentFragment();
      visible.forEach(item => {
        if (!item.el) item.el = this._createElement(item);
        item.el.classList.remove('hidden');
        frag.appendChild(item.el);
      });
      this._list.appendChild(frag);

      if (this._countEl) {
        const totalCount = this._starFilter !== null || this._archiveLoaded
          ? total
          : (this._totalCount || total);
        this._countEl.textContent = `${shown} of ${totalCount}`;
      }

      if (this._moreBtn) {
        const more = this._archiveUrl && !this._archiveLoaded
          ? shown < Math.max(total, this._totalCount)
          : shown < total;
        this._moreBtn.style.display = more ? '' : 'none';
      }
    }
  }

  if (!customElements.get('reviews-section')) {
    customElements.define('reviews-section', ReviewsSection);
  }
})();
